/**
 * Pose — the animator, reworked against the poses reference
 * (chars_poses.jpg: idle / run / attack / flinch / defeated).
 *
 * NO RIG. The assembler's named pivots ARE the rig: the reference bodies are
 * rigid blocks with visible seams that articulate at joints, so skinning
 * would buy smooth deformation the art style explicitly does not want, at the
 * cost of instancing (a SkinnedMesh needs per-instance bone state). A pose
 * here is a small table of pivot rotations; the runtime blends between
 * authored keyposes with snappy easing. Everything a pivot does is a plain
 * matrix, so the same code drives Groups (this prototype) or per-part
 * instance buffers (the pooled ship path).
 *
 * The base states, from the sheet:
 *   IDLE     symmetric stance, arms flared a touch, breathing
 *   RUN      a LUNGE, not a stroll — deep stride, torso pitched forward,
 *            front arm pumped up and bent; big amplitudes or it reads idle
 *   FLINCH   arched spine, head thrown back, both arms flung up — snap in
 *            (~60ms), ease out (~260ms)
 *   DEFEATED the toy FALLS APART: pivots detach (root.attach) and scatter to
 *            the floor as loose blocks, X-eyes on. Per-part construction
 *            makes this nearly free, and nothing else says "eliminated, pot
 *            settled" as clearly. Scatter is DETERMINISTIC — velocities come
 *            from the blueprint's seeded `scatterSeed` via the sim's RNG, so
 *            every client sees the same pieces land in the same pile.
 *
 * ── Casts: six archetypes keyed on the ability's `hit.kind` ─────────────
 *
 * One punch for everything made a 350ms ultimate and a 90ms basic look
 * identical. Authoring twenty bespoke clips would drift; instead every
 * ability animates as its SHAPE — the `HitShape` union in
 * @chaos/protocol/characters.ts is the natural animation axis, and any
 * ability added later inherits a correct animation by construction:
 *
 *   melee_cone   a swing ACROSS an arc — the torso yaws through it (yaw is
 *                the axis the 55° match camera sees at full amplitude)
 *   sphere       a radial burst — both arms rise, then drive down-and-OUT
 *                as the body drops; symmetric, so it reads "around me"
 *   dash_line    a committed lunge — coil low, then the whole body drives
 *                forward, both arms thrust out parallel along the line
 *   projectile   a throw — wound up behind the shoulder, off arm pointing
 *                down the line, one distinct RELEASE frame
 *   ground_zone  something PLACED — the body bows and the casting arm
 *                reaches DOWN to the floor; the downward reach is what
 *                separates "put it there" from "swung it at you"
 *   self         a buff — arms pull IN and cross, the body braces, then the
 *                chest pops open; nothing leaves the silhouette
 *
 * TIMING COMES FROM THE ABILITY, NOT FROM CONSTANTS. The chamber phase lasts
 * exactly `ability.windupMs` — the wind-up on the body IS the reactable half
 * of the cast, so Fault Line's 600ms coil must dwarf the basic's 90ms flick
 * or the animation is lying about the game state. The tail (hold + return)
 * is derived from `ability.recoveryMs`, the committed window. Only the
 * strike snap itself is authored, scaled by per-family flavour: the
 * juggernaut lands heavy and slow, the assassin flicks.
 *
 * ── Twin-stick split ─────────────────────────────────────────────────────
 * root yaw = AIM (what the server sends; where abilities will land).
 * legsRoot yaw = MOVE, in root-local space, clamped ±~63°; past ~105° the
 * legs flip 180° and the cycle reverses — a backpedal, not an owl-neck.
 * The run cycle and the aim are fully separable channels.
 *
 * Animation is presentation: it reads snapshot-derived values and never feeds
 * the sim. But it still avoids per-client randomness — a pose that differs
 * across screens reads as desync — so the idle phase offset and the death
 * scatter are both seeded draws.
 */

import { nextRange } from '@chaos/sim';
import { shortAngleDist, clamp, damp, lerp } from '../../math.js';
import { juiceFor, RECOIL_DECAY, deathSpinShrink } from '../../PlayerVisuals.js';

const TAU = Math.PI * 2;

const wrap = (a) => {
  while (a > Math.PI) a -= TAU;
  while (a < -Math.PI) a += TAU;
  return a;
};

const smooth = (k) => k * k * (3 - 2 * k); // smoothstep

const FLINCH_IN = 60;
const FLINCH_MS = 320;

/**
 * The run keypose, sheet frame 2 — RE-AUTHORED FOR THE MATCH CAMERA (55° from
 * above, ~18 out). From overhead the leg swing is mostly foreshortened away;
 * what actually reads as "running" is shoulder counter-rotation (yaw is the
 * one axis the camera sees at full amplitude), vertical bob timed to the
 * contact/flight phases, the forward lean, and the arm pump. So `twist` and
 * `bob` carry the read, and the legs are allowed to be slightly smaller.
 */
const RUN = {
  leg: 1.0, // stride ±, the lunge
  armFwd: -1.3, // front arm pumped up-and-bent
  armBack: 0.75, // back arm driving behind
  lean: 0.34, // torso pitched into the travel
  twist: 0.3, // shoulder counter-yaw — the overhead tell
  hipTwist: 0.12, // hips counter the shoulders
  bob: 0.13, // body high in flight (legs passing), low at contact
  legsBob: 0.05, // the lower body lifts a little too — flight, not a piston
};

/**
 * Cast archetypes. Two keyframes each — CHAMBER (reached over the ability's
 * own windupMs) and STRIKE (snapped to over `strikeMs`) — on a shared channel
 * set. Missing channels are 0 = neutral.
 *
 * Channels and sign conventions (empirical, from the run/flinch layers):
 *   armRX/armLX   arm pivot pitch: negative = forward/up, positive = back
 *   armRZ/armLZ   arm flare: armL positive = OUT, armR negative = OUT
 *   twist         upper yaw: positive = right shoulder pulled BACK
 *   lean          upper pitch (ADDITIVE): positive = forward
 *   rise          upper y offset (ADDITIVE): positive = up
 *   headX         head pitch (ADDITIVE): positive = bowed
 *   stanceL/R     leg pitch when the legs are free (standing casts only)
 *
 * Every keyframe is authored FOR THE MATCH CAMERA: yaw sweeps, symmetric arm
 * spreads, and vertical drops read at 55°-from-above; a side-on flourish
 * does not.
 */
const ARCHETYPES = {
  // Swing through an arc across the front. The torso yaws ~80° chamber→
  // follow-through and the arm sweeps out-back → across the body; the yaw
  // sweep is what the overhead camera actually sees as an arc.
  melee_cone: {
    strikeMs: 90,
    chamber: { armRX: 0.55, armRZ: -0.95, armLX: -0.45, twist: 0.6, lean: -0.08 },
    strike: {
      armRX: -1.5,
      armRZ: 0.3,
      armLX: 0.2,
      twist: -0.8,
      lean: 0.24,
      stanceL: -0.3,
      stanceR: 0.3,
    },
  },
  // Radial burst centred on the caster: both arms rise together, then drive
  // down-and-OUT as the body drops. Symmetry is the read — nothing about it
  // points at any one target.
  sphere: {
    strikeMs: 110,
    chamber: {
      armRX: -2.3,
      armRZ: -0.35,
      armLX: -2.3,
      armLZ: 0.35,
      rise: 0.12,
      lean: -0.18,
      headX: -0.2,
    },
    strike: {
      armRX: -0.2,
      armRZ: -1.1,
      armLX: -0.2,
      armLZ: 1.1,
      rise: -0.24,
      lean: 0.3,
      headX: 0.2,
      stanceL: -0.4,
      stanceR: 0.4,
    },
  },
  // Committed lunge along a line: coil low and back, then the whole body
  // drives forward with both arms thrust out parallel — the silhouette
  // elongates along the line the hit resolves on.
  dash_line: {
    strikeMs: 100,
    chamber: { armRX: 0.8, armLX: 0.8, rise: -0.12, lean: -0.3, stanceL: -0.45, stanceR: 0.25 },
    strike: {
      armRX: -1.55,
      armRZ: 0.15,
      armLX: -1.55,
      armLZ: -0.15,
      rise: -0.08,
      lean: 0.5,
      headX: -0.25,
      stanceL: 0.5,
      stanceR: -0.6,
    },
  },
  // A throw: wound up high behind the shoulder with a big chamber twist, the
  // off arm POINTING down the line (the aim read from overhead), then one
  // fast release frame snapping the arm over the top.
  projectile: {
    strikeMs: 80,
    chamber: { armRX: 1.7, armRZ: -0.55, armLX: -1.1, twist: 0.75, lean: -0.16 },
    strike: {
      armRX: -1.35,
      armRZ: -0.1,
      armLX: -0.25,
      twist: -0.6,
      lean: 0.3,
      stanceL: -0.35,
      stanceR: 0.35,
    },
  },
  // Placed on the floor: raise, then BOW — the body drops and pitches far
  // forward while the casting arm drives DOWN to the ground in front. The
  // downward reach plus the vertical drop (which the overhead camera sees at
  // full size) is what makes "placed" legible.
  ground_zone: {
    strikeMs: 120,
    chamber: { armRX: -1.9, rise: 0.1, lean: -0.12, headX: -0.25 },
    strike: {
      armRX: -0.55,
      armLX: 0.5,
      armLZ: 0.55,
      rise: -0.3,
      lean: 0.66,
      headX: 0.35,
      stanceL: -0.5,
      stanceR: 0.4,
    },
  },
  // A buff: nothing leaves the body. Arms pull IN and cross over the chest,
  // the body sinks into the brace, then the chest pops open on release.
  self: {
    strikeMs: 90,
    chamber: {
      armRX: -1.15,
      armRZ: 0.55,
      armLX: -1.15,
      armLZ: -0.55,
      rise: -0.1,
      lean: 0.08,
      headX: 0.18,
    },
    strike: {
      armRX: -0.35,
      armRZ: -0.5,
      armLX: -0.35,
      armLZ: 0.5,
      rise: 0.12,
      lean: -0.14,
      headX: -0.15,
    },
  },
};

// A basic-attack bullet is thrown from the same pose as a shell.
ARCHETYPES.shot = ARCHETYPES.projectile;

/**
 * Per-family flavour ON TOP of the archetypes — secondary by design, and it
 * never touches the windup (that is gameplay data). `snap` scales the strike
 * itself, `amp` the keyframe amplitudes, `stance` how wide the feet plant.
 * The rest of "heavy vs light" comes from the data for free: the juggernaut's
 * recoveries are the longest on the roster, so his returns are too.
 */
const FLAVOR = {
  volt: { snap: 0.75, amp: 0.92 }, // assassin: quick, light
  monolith: { snap: 1.4, amp: 1.12, stance: 1.35 }, // juggernaut: heavy, rooted
  hex: { snap: 1.0, amp: 1.0 },
  mirage: { snap: 0.85, amp: 0.9 }, // trickster: loose, fast
  talon: { snap: 1.05, amp: 1.0 },
};

const DASH_MS_DEFAULT = 150; // DASH_DURATION_MS; callers pass the real value
const DASH_SETTLE_MS = 200;

const GRAVITY = 14;

export function createPoseDriver(group) {
  const rig = group.userData.rig;
  const bp = group.userData.blueprint ?? {};
  const phaseOffset = (bp.phaseOffset ?? 0) * TAU;
  const flavor = FLAVOR[bp.family] ?? {};
  // Weight by archetype: the tank's kick and flinch are half the assassin's.
  const juice = juiceFor(group.userData.characterId ?? bp.family);

  const state = {
    phase: 0,
    runWeight: 0,
    legsYaw: 0,
    backpedal: false,
    // Velocity is smoothed inside the driver: remote velocities step at
    // snapshot rate (15Hz) and raw steps twitch the legs and flip the
    // backpedal. ~60ms of smoothing is invisible and kills both.
    smVx: 0,
    smVz: 0,
    cast: null, // { at, arch, windup, strike, hold, recover, amp, stance }
    dashAt: -1e9,
    dashMs: DASH_MS_DEFAULT,
    flinchAt: -1e9,
    /** 1 on the strike frame of a cast, relaxing at RECOIL_DECAY. */
    recoil: 0,
    /** Seconds since death, for the spin-shrink. */
    deadT: 0,
    dead: false,
    pieces: null, // [{node, vel, angVel, restY, settled}] once scattered
    restore: null, // pre-scatter parent/transform of each unit, for revive()
  };

  // ── Defeat: the toy falls apart ─────────────────────────────

  function scatter() {
    const rng = { rngState: (bp.scatterSeed ?? 1) >>> 0 };
    state.pieces = [];
    // Recorded before the detach so revive() can put the toy back together —
    // the gym and scrims respawn players, and a wagered match never calls it.
    state.restore = rig.scatterUnits.map((unit) => ({
      node: unit,
      parent: unit.parent,
      pos: unit.position.clone(),
      quat: unit.quaternion.clone(),
      scale: unit.scale.clone(),
    }));
    for (const unit of rig.scatterUnits) {
      // Flatten into root space, preserving the world transform mid-pose.
      rig.root.attach(unit);
      const a = nextRange(rng, 0, TAU);
      // Tight: the pieces must read as ONE downed toy, not shrapnel — at 8
      // players a wide scatter mixes neighbouring piles.
      const speed = nextRange(rng, 0.7, 1.9);
      state.pieces.push({
        node: unit,
        vel: {
          x: Math.sin(a) * speed,
          y: nextRange(rng, 2.2, 4.2),
          z: Math.cos(a) * speed,
        },
        angVel: {
          x: nextRange(rng, -5, 5),
          y: nextRange(rng, -5, 5),
          z: nextRange(rng, -5, 5),
        },
        restY: -1 + nextRange(rng, 0.12, 0.2), // pieces are ~0.3 blocks
        settled: false,
      });
    }
    const eyes = rig.headPivot.getObjectByName('eyes');
    const xEyes = rig.headPivot.getObjectByName('xEyes');
    if (eyes) eyes.visible = false;
    if (xEyes) xEyes.visible = true;
    group.rotation.x = 0;
  }

  function updateScatter(dt) {
    for (const p of state.pieces) {
      if (p.settled) continue;
      p.vel.y -= GRAVITY * dt;
      p.node.position.x += p.vel.x * dt;
      p.node.position.y += p.vel.y * dt;
      p.node.position.z += p.vel.z * dt;
      p.node.rotation.x += p.angVel.x * dt;
      p.node.rotation.y += p.angVel.y * dt;
      p.node.rotation.z += p.angVel.z * dt;
      if (p.node.position.y <= p.restY && p.vel.y < 0) {
        p.node.position.y = p.restY;
        // One soft bounce, then friction kills it — blocks, not rubber.
        p.vel.y = -p.vel.y * 0.25;
        p.vel.x *= 0.4;
        p.vel.z *= 0.4;
        p.angVel.x *= 0.35;
        p.angVel.y *= 0.35;
        p.angVel.z *= 0.35;
        if (Math.abs(p.vel.y) < 0.6) {
          p.settled = true;
          // Rest flat-ish: blocks land on a face, not on a corner.
          p.node.rotation.x = Math.round(p.node.rotation.x / (Math.PI / 2)) * (Math.PI / 2);
          p.node.rotation.z = Math.round(p.node.rotation.z / (Math.PI / 2)) * (Math.PI / 2);
        }
      }
    }
  }

  // ── The frame update ────────────────────────────────────────

  function update(now, dt, { vx = 0, vz = 0, aimYaw = 0 } = {}) {
    if (state.dead) {
      // The toy falls apart AND the whole thing spins down to nothing: the
      // pieces fly for the first third of a second, shrinking with the group,
      // and the defeat burst (fx.defeat) takes over from there.
      updateScatter(dt);
      state.deadT += dt;
      deathSpinShrink(group, state.deadT, dt);
      return;
    }

    const t = now / 1000;
    group.rotation.y = aimYaw;
    group.rotation.x = 0;

    // ── Dash weight, first: it damps the run cycle below ─────
    // Snap in over ~40ms, hold through the sim's dash window, settle out.
    const dashAge = now - state.dashAt;
    let dashW = 0;
    if (dashAge < state.dashMs + DASH_SETTLE_MS) {
      const inW = Math.min(1, dashAge / 40);
      const outW = dashAge < state.dashMs ? 1 : 1 - smooth((dashAge - state.dashMs) / DASH_SETTLE_MS);
      dashW = inW * outW;
    }

    // Smooth the velocity the pose sees. Remote velocities arrive in 15Hz
    // steps and the local predictor's flip instantly on a key tap; raw values
    // twitch the legs and rattle the backpedal boundary.
    const k = damp(dt, 1e-7); // ~60ms to converge
    state.smVx += (vx - state.smVx) * k;
    state.smVz += (vz - state.smVz) * k;
    const speed = Math.hypot(state.smVx, state.smVz);
    const moving = speed > 0.8;
    state.runWeight += ((moving ? Math.min(1, speed / 5) : 0) - state.runWeight) * damp(dt, 1e-4);
    const w = state.runWeight;
    const iw = 1 - w;

    // Legs face the run; body faces the aim. The flip into backpedal has
    // hysteresis (enter past ~112°, leave under 90°) so strafing along the
    // boundary doesn't slam the legs back and forth every snapshot.
    let legsTarget = 0;
    if (moving) {
      const moveYaw = Math.atan2(state.smVx, state.smVz);
      let delta = shortAngleDist(aimYaw, moveYaw);
      const a = Math.abs(delta);
      state.backpedal = state.backpedal ? a > Math.PI * 0.5 : a > Math.PI * 0.62;
      if (state.backpedal) delta = wrap(delta - Math.PI);
      legsTarget = clamp(delta, -1.1, 1.1);
    } else {
      state.backpedal = false;
    }
    const backpedal = state.backpedal;
    state.legsYaw += shortAngleDist(state.legsYaw, legsTarget) * damp(dt, 0.0005);

    // ── Locomotion: blend idle keypose ↔ run keypose ─────────
    // Cadence rides the actual ground speed: WALK 12 ≈ 1.9 strides/s, SPRINT
    // 19 ≈ 2.6. The old fixed-ish rate hit 3 strides/s at a walk — a scuttle.
    // A dash spikes the speed to 42, which would flail the legs at 30 rad/s —
    // the dash layer owns the limbs for that window, so the cycle is damped
    // rather than driven by the spike.
    const dir = backpedal ? -1 : 1;
    state.phase += dt * (4.2 + speed * 0.62) * dir * Math.max(w, 0.001) * (1 - dashW * 0.85);
    const s = Math.sin(state.phase);
    // Contact at |s|=1 (the lunge frame, legs split), flight at s=0 (legs
    // passing). The body rides high in flight and drops into the contact —
    // from 55° above this bob IS most of what reads as running.
    const flight = 0.5 + 0.5 * Math.cos(state.phase * 2);

    rig.legL.rotation.x = s * RUN.leg * w;
    rig.legR.rotation.x = -s * RUN.leg * w;

    // Front arm pumps up-and-bent, back arm drives behind — asymmetric range,
    // which is most of the difference between the sheet's run and a sway.
    const armPhase = -s; // arms counter the legs
    const fwdAmt = Math.max(0, armPhase);
    const backAmt = Math.max(0, -armPhase);
    const idleArmX = 0.08 + Math.sin(t * 1.4 + phaseOffset) * 0.045;
    rig.armL.rotation.x = (fwdAmt * RUN.armFwd + backAmt * RUN.armBack) * w + idleArmX * iw;
    rig.armR.rotation.x = (backAmt * RUN.armFwd + fwdAmt * RUN.armBack) * w + idleArmX * iw;
    rig.armL.rotation.z = 0.18 * iw + 0.07 * w;
    rig.armR.rotation.z = -0.18 * iw - 0.07 * w;

    rig.upper.position.y = flight * RUN.bob * w;
    rig.upper.position.z = 0;
    rig.legsRoot.position.y = flight * RUN.legsBob * w;
    // The lean goes INTO THE TRAVEL, not the aim: `upper` lives in the aim
    // frame, so the travel-lean is decomposed along the legs yaw — a strafing
    // body tips sideways over its running legs instead of bowing at its
    // target.
    const leanAmt = RUN.lean * w * (backpedal ? -0.55 : 1);
    rig.upper.rotation.x = Math.cos(state.legsYaw) * leanAmt;
    const travelRoll = -Math.sin(state.legsYaw) * leanAmt;
    // Shoulders counter-rotate against the hips: yaw is the one axis the
    // overhead camera sees at full size, so this pair carries the cycle.
    rig.upper.rotation.y = s * -RUN.twist * w;
    rig.legsRoot.rotation.y = state.legsYaw + s * RUN.hipTwist * w;

    // Idle life (sheet frame 1): breathing, a slow weight shift, small head
    // sway. Slow and small on purpose — life, not fidget. Phase-offset by the
    // seeded value so eight idlers never pulse in lockstep.
    rig.upper.scale.y = 1 + Math.sin(t * 2.2 + phaseOffset) * 0.016 * iw;
    rig.upper.rotation.z = travelRoll + Math.sin(t * 0.7 + phaseOffset) * 0.022 * iw;
    rig.headPivot.rotation.z = Math.sin(t * 1.2 + phaseOffset) * 0.035 * iw;
    rig.headPivot.rotation.x = 0;

    // ── Cast layer — the ability's shape, on the ability's clock ──
    // chamber (windupMs, the reactable telegraph) → strike (the archetype's
    // snap × flavour) → hold → hand back to locomotion over the ability's
    // recovery. Blended in over 50ms so starting a cast mid-run doesn't snap
    // the arm from a pump to a chamber in one frame; handed back by FADING
    // AUTHORITY rather than driving to zero, so the run reclaims the arms
    // smoothly.
    const c = state.cast;
    if (c) {
      const at = now - c.at;
      const total = c.windup + c.strike + c.hold + c.recover;
      if (at >= total) {
        state.cast = null;
      } else {
        const blend = Math.min(1, at / 50);
        let wc = 0; // chamber weight
        let ws = 0; // strike weight
        let auth = 1; // layer authority
        if (at >= c.windup && !c.kicked) {
          // The strike frame: the body kicks back from its own shot.
          c.kicked = true;
          state.recoil = 1;
        }
        if (at < c.windup) {
          // Ease most of the way in fast, then keep creeping deeper until
          // release — a 600ms windup holds a LIVE coil, not a freeze-frame.
          const kk = at / c.windup;
          wc = kk < 0.6 ? (1 - (1 - kk / 0.6) * (1 - kk / 0.6)) * 0.85 : 0.85 + (0.15 * (kk - 0.6)) / 0.4;
        } else if (at < c.windup + c.strike) {
          const kk = (at - c.windup) / c.strike;
          const e = 1 - (1 - kk) * (1 - kk) * (1 - kk); // fast out
          wc = 1 - e;
          ws = e;
        } else if (at < c.windup + c.strike + c.hold) {
          ws = 1;
        } else {
          ws = 1;
          auth = 1 - smooth((at - c.windup - c.strike - c.hold) / c.recover);
        }
        const A = c.arch;
        const g = (key) => ((A.chamber[key] ?? 0) * wc + (A.strike[key] ?? 0) * ws) * c.amp;
        const f = blend * auth;
        rig.armR.rotation.x = lerp(rig.armR.rotation.x, g('armRX'), f);
        rig.armR.rotation.z = lerp(rig.armR.rotation.z, g('armRZ'), f);
        rig.armL.rotation.x = lerp(rig.armL.rotation.x, g('armLX'), f);
        rig.armL.rotation.z = lerp(rig.armL.rotation.z, g('armLZ'), f);
        rig.upper.rotation.y = lerp(rig.upper.rotation.y, g('twist'), f);
        rig.upper.rotation.x += g('lean') * f;
        rig.upper.position.y += g('rise') * f;
        rig.headPivot.rotation.x += g('headX') * f;
        if (!moving) {
          // Plant the feet under the cast when the legs are free to take it.
          rig.legL.rotation.x = lerp(rig.legL.rotation.x, g('stanceL') * c.stance, f);
          rig.legR.rotation.x = lerp(rig.legR.rotation.x, g('stanceR') * c.stance, f);
        }
      }
    }

    // ── Recoil: the kick from a cast, on top of whatever pose owns the body ──
    // Pitch back and pull the chest back, relaxing at RECOIL_DECAY. Weighted by
    // archetype, so the tank barely moves and the assassin snaps.
    if (state.recoil > 0.005) {
      state.recoil *= Math.exp(-RECOIL_DECAY * dt);
      const r = state.recoil * juice.recoil;
      rig.upper.rotation.x -= 0.2 * r;
      rig.upper.position.z -= 0.12 * r;
    } else {
      state.recoil = 0;
    }

    // ── Dash layer (i-frames — every character has it) ───────
    // The whole body commits: a hard lean INTO THE TRAVEL (decomposed the
    // same way the run lean is, so a sideways dash tips sideways and a
    // backpedal dash falls backward), arms swept behind, legs tucked into a
    // held lunge. Overrides the cast layer for its 150ms — the sim's dash
    // interrupts casts too.
    if (dashW > 0) {
      const travelYaw = backpedal ? state.legsYaw + Math.PI : state.legsYaw;
      const dLean = 0.8 * dashW;
      rig.upper.rotation.x += Math.cos(travelYaw) * dLean;
      rig.upper.rotation.z += -Math.sin(travelYaw) * dLean;
      rig.armL.rotation.x = lerp(rig.armL.rotation.x, 1.5, dashW);
      rig.armR.rotation.x = lerp(rig.armR.rotation.x, 1.5, dashW);
      rig.armL.rotation.z = lerp(rig.armL.rotation.z, 0.4, dashW);
      rig.armR.rotation.z = lerp(rig.armR.rotation.z, -0.4, dashW);
      // Held lunge, not a cycle: one leg driving, one trailing.
      rig.legL.rotation.x = lerp(rig.legL.rotation.x, 0.9, dashW);
      rig.legR.rotation.x = lerp(rig.legR.rotation.x, -0.55, dashW);
      rig.upper.position.y += -0.1 * dashW;
      rig.headPivot.rotation.x += -0.35 * dashW; // eyes up, on the exit
    }

    // ── Flinch layer (sheet frame 4) — arch back, arms flung ─
    // Arms fling OUT to the sides rather than straight up: from the match
    // camera a raised arm points at the lens and vanishes; a spread one
    // doubles the silhouette's width, which is what makes the hit legible.
    const ht = now - state.flinchAt;
    if (ht < FLINCH_MS) {
      const d = (ht < FLINCH_IN ? ht / FLINCH_IN : 1 - (ht - FLINCH_IN) / (FLINCH_MS - FLINCH_IN)) * juice.flinch;
      rig.upper.rotation.x = -0.42 * d;
      rig.upper.position.z = -0.12 * d; // recoil off the impact
      rig.headPivot.rotation.x = -0.5 * d;
      rig.armL.rotation.x = -0.7 * d;
      rig.armR.rotation.x = -0.6 * d;
      rig.armL.rotation.z = 1.15 * d;
      rig.armR.rotation.z = -1.05 * d;
    }
  }

  /**
   * Fire the cast layer for one ability. The archetype comes from
   * `ability.hit.kind`, the chamber from `ability.windupMs` (EXACT — zero
   * windup means zero chamber: drawing a wind-up an opponent cannot actually
   * react to would lie in the other direction), the tail from
   * `ability.recoveryMs` with a floor so even a 0-recovery buff eases out
   * instead of popping.
   */
  function cast(now, ability) {
    if (!ability) return;
    const arch = ARCHETYPES[ability.hit?.kind] ?? ARCHETYPES.melee_cone;
    const tail = Math.max(160, ability.recoveryMs ?? 0);
    const hold = Math.min(90, tail * 0.35);
    state.cast = {
      at: now,
      arch,
      windup: Math.max(0, ability.windupMs ?? 0),
      strike: Math.round(arch.strikeMs * (flavor.snap ?? 1)),
      hold,
      recover: tail - hold,
      amp: flavor.amp ?? 1,
      stance: flavor.stance ?? 1,
      kicked: false,
    };
  }

  return {
    update,
    cast,
    /**
     * The shared dash — i-frames, on every character, previously the one
     * move with no pose at all. `durationMs` is the sim's DASH_DURATION_MS,
     * passed by the caller so this file doesn't hardcode a gameplay number.
     */
    dash(now, durationMs = DASH_MS_DEFAULT) {
      state.dashAt = now;
      state.dashMs = Math.max(60, durationMs);
    },
    hit(now) {
      state.flinchAt = now;
    },
    die() {
      if (state.dead) return;
      state.dead = true;
      scatter();
    },
    /**
     * Puts the toy back together after a scatter — the exact inverse of the
     * detach, restoring each unit's recorded parent and local transform. Only
     * the gym and scrims ever call this; a wagered death is final.
     */
    revive() {
      if (!state.dead) return;
      group.scale.set(1, 1, 1);
      state.deadT = 0;
      state.recoil = 0;
      for (const r of state.restore ?? []) {
        r.parent.add(r.node);
        r.node.position.copy(r.pos);
        r.node.quaternion.copy(r.quat);
        r.node.scale.copy(r.scale);
      }
      state.restore = null;
      state.pieces = null;
      state.dead = false;
      const eyes = rig.headPivot.getObjectByName('eyes');
      const xEyes = rig.headPivot.getObjectByName('xEyes');
      if (eyes) eyes.visible = true;
      if (xEyes) xEyes.visible = false;
    },
    get dead() {
      return state.dead;
    },
  };
}
